import { Component } from '@theme/component';
import { queryRecommendations } from '@theme/query-recommendations';
import { renderProductCard, initCardInteractions } from '@theme/ng-product-card';

/**
 * Custom Product Recommendations (API).
 *
 * Drives sections/custom-product-recommendations-api.liquid — but only on its API
 * fallback path: when the product has customMA.alternateProductAssociations the
 * section renders those server-side and never emits this element. Lazy-loads "more
 * like this" recommendations from the recommendation API (anchored on the current
 * product's single akeneo ID) when the section nears the viewport, renders each hit
 * with the shared NG
 * product card, and injects a <carousel-component> (with the slides) into the mount
 * point — the carousel-component then initialises Swiper itself, wired to the
 * server-rendered arrows via the passed-through data-settings.
 *
 * @extends {Component}
 */
class CustomProductRecommendationsApi extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  /** Hide the whole section — no anchor, no results, or an error. */
  #hide() {
    this.hidden = true;
  }

  async #load() {
    const akeneoId = this.dataset.akeneoId;
    const limit = Number(this.dataset.limit) || 10;
    const mount = this.querySelector('[data-carousel-mount]');

    if (!akeneoId || !mount) {
      this.#hide();
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const result = await queryRecommendations({
        akeneoProductIds: [akeneoId],
        size: limit,
        signal: this.#abortController.signal,
      });

      if (!result.ok || !result.hits.length) {
        this.#hide();
        return;
      }

      // Dedupe by Shopify product ID and exclude the current product.
      const seen = new Set([this.dataset.productId || '']);
      const slides = [];
      for (const hit of result.hits) {
        const shopifyId = String(hit.shopifyProductId || '');
        if (!shopifyId || seen.has(shopifyId)) continue;
        seen.add(shopifyId);

        const card = renderProductCard(hit);
        if (card) slides.push(`<div class="swiper-slide tw:!h-auto">${card}</div>`);
        if (slides.length >= limit) break;
      }

      if (!slides.length) {
        this.#hide();
        return;
      }

      const settings = this.dataset.settings || '{}';
      // Injecting the <carousel-component> with slides present lets its own
      // connectedCallback initialise Swiper against the final slide set.
      mount.innerHTML = `
        <carousel-component class="tw:block tw:w-full" data-settings='${settings}'>
          <div class="tw:relative tw:overflow-visible">
            <div class="swiper tw:w-full tw:relative">
              <div class="swiper-wrapper">${slides.join('')}</div>
            </div>
          </div>
        </carousel-component>`;

      initCardInteractions(mount);
      this.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Custom product recommendations (API) failed to load:', error);
        this.#hide();
      }
    }
  }
}

if (!customElements.get('custom-product-recommendations-api')) {
  customElements.define('custom-product-recommendations-api', CustomProductRecommendationsApi);
}
